import React, { useCallback } from 'react';

import { Journey } from 'src/common/interfaces/journey.interface';
import { Node } from 'src/common/interfaces/node.interface';

import { PlayerContentAnnotationTooltip } from '../content-annotation-tooltip';
import { PlayerNavigator } from '../navigator';
import { getNodeAnnotation, hasAnnotation } from 'src/common/helpers/annotation';
import { PlayerChatWidget, PlayerChatWidgetProps } from '../chat/widget';
import { PlayerChatButton } from '../chat/button';
import { DownloadStrongIcon } from 'src/monet/icons';
import { apiGetFileAssetDownloadUrl } from 'src/utils/journeyApi';
import { ReportAbuseLink } from '../report-abuse-link';

interface PlayerNavigationDesktopBottomProps
  extends Pick<PlayerChatWidgetProps, 'userEventsFactory' | 'isEmailGateUnlocked' | 'emailRequired'> {
  journey: Journey;
  currentNode: Node;
  isChatEnabled: boolean;
  isFirstNode: boolean;
  isLastNode: boolean;
  onClickPrevious: () => void;
  onClickNext: () => void;
  onNavigateToNode: (node: any) => void;
}

export const PlayerNavigationDesktopBottom = ({
  journey,
  currentNode,
  isFirstNode,
  isLastNode,
  onClickPrevious,
  onClickNext,
  isChatEnabled,
  onNavigateToNode,
  isEmailGateUnlocked,
  emailRequired,
  userEventsFactory,
}: PlayerNavigationDesktopBottomProps) => {
  const handlePdfDownload = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();

      if (!currentNode?.file_asset?.id) {
        // Fallback to original behavior if no file asset ID
        if (currentNode?.file_asset?.url) {
          window.open(currentNode.file_asset.url, '_blank');
        }
        return;
      }

      try {
        const response = await apiGetFileAssetDownloadUrl(currentNode.file_asset.id);
        if (response.download_url) {
          // Create a temporary link to trigger download with proper filename
          const downloadLink = document.createElement('a');
          downloadLink.href = response.download_url;
          downloadLink.download = response.filename || 'download';
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
        } else {
          // Fallback to original behavior if API fails
          if (currentNode.file_asset.url) {
            window.open(currentNode.file_asset.url, '_blank');
          }
        }
      } catch (error) {
        console.error('Failed to get download URL, falling back to direct link:', error);
        // Fallback to original behavior if API fails
        if (currentNode.file_asset.url) {
          window.open(currentNode.file_asset.url, '_blank');
        }
      }
    },
    [currentNode?.file_asset?.id, currentNode?.file_asset?.url]
  );

  if (!currentNode || journey.feature_flags.hide_desktop_navigation) {
    return null;
  }

  const isContentPDF = !!currentNode && currentNode.content_type === 'pdf';
  const isContentPDFDownloadable = isContentPDF && currentNode.data?.downloadable;

  return (
    <div className='hidden md:flex flex-none w-full min-h-0 flex-col space-y-4'>
      <div className='flex items-center justify-between px-6 md:px-0 md:mb-0'>
        <div className='flex items-center gap-2'>
          {isChatEnabled ? (
            <PlayerChatWidget
              isEmailGateUnlocked={isEmailGateUnlocked}
              emailRequired={emailRequired}
              journey={journey}
              stepUUID={currentNode.uuid}
              userEventsFactory={userEventsFactory}
              renderOrigin={({ activeChatStack, onClick }) => (
                <PlayerChatButton messageCountCopy={activeChatStack.messageCountCopy} onClick={onClick} />
              )}
            />
          ) : (
            <div className='flex w-fit' />
          )}
          {/* Abuse reporting (JOU-42). The sandbox stops a hosted artifact
              attacking Journey; it does nothing about someone sharing a
              convincing fake page on a jny.io link. The remedy is letting the
              viewer tell us, so the link has to live where the viewer is. */}
          {journey.alias?.uuid && <ReportAbuseLink aliasUUID={journey.alias.uuid} />}
          {isContentPDFDownloadable && currentNode.file_asset && (
            <a
              download
              href={currentNode.file_asset.url}
              onClick={handlePdfDownload}
              target='_blank'
              rel='noopener noreferrer'
              className='flex w-fit cursor-pointer items-center justify-center space-x-1.5 min-w-[4rem] bg-bedrock-light-gray py-3.5 px-4 rounded-[2.5rem] h-12 has-hover'
            >
              <DownloadStrongIcon />
            </a>
          )}
        </div>
        {hasAnnotation(currentNode) && (
          <PlayerContentAnnotationTooltip
            creator={currentNode.creator}
            annotation={getNodeAnnotation(currentNode)}
            onNavigateToNode={onNavigateToNode}
          />
        )}
        <div className='hidden md:flex flex-grow' style={{ maxWidth: 150 }}>
          <PlayerNavigator
            isFirst={isFirstNode}
            isLast={isLastNode}
            onClickPrevious={onClickPrevious}
            onClickNext={onClickNext}
          />
        </div>
      </div>
    </div>
  );
};
